/**
 * Floating Product Video Player
 * ------------------------------------------------------------------
 * Web component powering snippets/floating-product-video.liquid.
 * Only ever instantiated on product pages where a video was found,
 * so this file has zero effect/cost on any other page.
 *
 * Responsibilities:
 *  - Autoplay the video muted, looped, inline.
 *  - Track the product media gallery's on-screen position and keep
 *    the floating player pinned to its bottom-right corner, until the
 *    customer drags it - after that, it stays where they put it.
 *  - Let the customer drag the mini player anywhere on screen (mouse,
 *    touch, or arrow keys), while still nudging out of the way of the
 *    sticky add-to-cart bar if it would end up covering it.
 *  - Hide/reposition itself (before it's been dragged) whenever it
 *    would overlap the sticky add-to-cart bar, the next section on
 *    the page (e.g. product description), or when the gallery itself
 *    scrolls out of view.
 *  - Provide play/pause, mute/unmute, close and expand controls.
 *  - Expand into a lightbox that preserves the video's native aspect
 *    ratio (no cropping) by moving the SAME <video> element into the
 *    lightbox markup, so the asset is never loaded twice. A dedicated
 *    minimize control (not a close/X) returns it to the mini player.
 *
 * IMPORTANT: all on-screen positioning happens on
 * `.floating-product-video__player`, never on the host element
 * itself. Applying `transform` to the host would create a CSS
 * containing block that traps the fixed-position lightbox inside the
 * small mini-player box instead of letting it cover the viewport.
 *
 * Isolation / safety notes (see accompanying audit for full detail):
 *  - No eval, no innerHTML, no external requests, no storage APIs, no
 *    analytics/tracking of any kind.
 *  - Custom element tag and internal state classes are prefixed to
 *    avoid clashing with unrelated theme/app code.
 *  - Every failure path (missing elements, blocked autoplay, a video
 *    that errors out, an unexpected exception) hides this feature
 *    quietly rather than throwing - it can never break the rest of
 *    the product page.
 */
(function () {
  if (typeof window === 'undefined' || !window.customElements) return;
  if (customElements.get('anuka-floating-video')) return;

  const KEYBOARD_NUDGE_STEP = 24;
  const LOG_PREFIX = '[anuka-floating-video]';

  class AnukaFloatingVideo extends HTMLElement {
    constructor() {
      super();
      this._raf = null;
      this._closed = false;
      this._userPositioned = false;
      this._dragging = false;
      this._x = 0;
      this._y = 0;
      this._wasPlayingBeforeExpand = true;
      this._wasMutedBeforeExpand = true;
      this._lastFocused = null;

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onPlayerKeydown = this._onPlayerKeydown.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onVideoError = this._onVideoError.bind(this);
    }

    connectedCallback() {
      // Defensive top-level guard: if anything here throws (e.g. a future
      // theme/markup change removes an element this code expects), fail
      // silently and never let it break the rest of the product page.
      try {
        this._setup();
      } catch (error) {
        this._safeFail(error);
      }
    }

    _setup() {
      this.player = this.querySelector('.floating-product-video__player');
      this.video = this.querySelector('.floating-product-video__video');
      this.miniFrame = this.querySelector('.floating-product-video__frame');
      this.lightbox = this.querySelector('.floating-product-video__lightbox');
      this.lightboxFrame = this.querySelector('.floating-product-video__lightbox-frame');
      this.backdrop = this.querySelector('[data-fpv-backdrop]');

      this.playBtn = this.querySelector('[data-fpv-play]');
      this.muteBtn = this.querySelector('[data-fpv-mute]');
      this.expandBtn = this.querySelector('[data-fpv-expand]');
      this.closeBtn = this.querySelector('[data-fpv-close]');
      this.lightboxPlayBtn = this.querySelector('[data-fpv-lightbox-play]');
      this.lightboxMuteBtn = this.querySelector('[data-fpv-lightbox-mute]');
      this.lightboxMinimizeBtn = this.querySelector('[data-fpv-minimize]');

      this.gallery = document.querySelector(this.dataset.gallerySelector || '.product__media');
      this.stickyForm = document.querySelector(this.dataset.stickySelector || '.product-sticky-form');

      // The "next section after the gallery" is treated as a hard lower
      // boundary the player must never cover (typically product description).
      this.boundaryEl = null;
      const gallerySection = this.gallery && this.gallery.closest('.shopify-section');
      if (gallerySection && gallerySection.nextElementSibling) {
        this.boundaryEl = gallerySection.nextElementSibling;
      }

      // Nothing sane to anchor to, or the markup this script depends on is
      // incomplete/missing - bail out quietly, no console errors, no
      // partially-broken UI left behind.
      if (!this.gallery || !this.video || !this.player || !this.lightbox || !this.lightboxFrame || !this.miniFrame) {
        this._safeFail(null);
        return;
      }

      this._bindEvents();
      this._attemptAutoplay();
      this._startTracking();
    }

    disconnectedCallback() {
      // Belt-and-suspenders cleanup in case the element is removed from the
      // DOM (e.g. a future dynamic section re-render) while mid-interaction,
      // so nothing is left listening on `window`/`document` forever.
      this._stopTracking();
      document.removeEventListener('keydown', this._onKeydown);
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = '';
      }
      if (this.player) {
        this.player.removeEventListener('pointermove', this._onPointerMove);
        this.player.removeEventListener('pointerup', this._onPointerUp);
        this.player.removeEventListener('pointercancel', this._onPointerUp);
      }
      this._dragging = false;
    }

    // Hides the whole feature without ever throwing. Used both for expected
    // "nothing to do here" cases and for unexpected runtime errors.
    _safeFail(error) {
      if (error && window.console && typeof console.warn === 'function') {
        console.warn(LOG_PREFIX + ' disabled itself after an unexpected error:', error);
      }
      this._closed = true;
      this._stopTracking();
      try {
        if (this.video) this.video.pause();
      } catch (e) {
        /* ignore */
      }
      this.classList.remove('fpv-is-visible');
      this.setAttribute('hidden', '');
    }

    _bindEvents() {
      if (this.playBtn) this.playBtn.addEventListener('click', () => this._togglePlay());
      if (this.muteBtn) this.muteBtn.addEventListener('click', () => this._toggleMute());
      if (this.expandBtn) this.expandBtn.addEventListener('click', () => this._expand());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this._close());

      if (this.lightboxPlayBtn) this.lightboxPlayBtn.addEventListener('click', () => this._togglePlay());
      if (this.lightboxMuteBtn) this.lightboxMuteBtn.addEventListener('click', () => this._toggleMute());
      if (this.lightboxMinimizeBtn) this.lightboxMinimizeBtn.addEventListener('click', () => this._collapse());
      if (this.backdrop) this.backdrop.addEventListener('click', () => this._collapse());

      this.video.addEventListener('play', () => this._syncPlayState());
      this.video.addEventListener('pause', () => this._syncPlayState());
      this.video.addEventListener('volumechange', () => this._syncMuteState());
      // If the video can't load (bad/unavailable URL, unsupported format,
      // network failure), disappear cleanly instead of showing a broken
      // black box - the rest of the product page is unaffected either way.
      this.video.addEventListener('error', this._onVideoError);

      // Dragging - mouse, touch and pen all go through Pointer Events.
      this.player.addEventListener('pointerdown', this._onPointerDown);
      this.player.addEventListener('keydown', this._onPlayerKeydown);
    }

    _onVideoError() {
      this._close();
    }

    _attemptAutoplay() {
      let playPromise;
      try {
        playPromise = this.video.play();
      } catch (e) {
        this._syncPlayState();
        return;
      }
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // Autoplay was blocked by the browser; player just starts paused.
          this._syncPlayState();
        });
      }
    }

    _togglePlay() {
      if (this.video.paused) {
        this.video.play().catch(() => {});
      } else {
        this.video.pause();
      }
    }

    _toggleMute() {
      this.video.muted = !this.video.muted;
    }

    _syncPlayState() {
      const playing = !this.video.paused;
      [this.playBtn, this.lightboxPlayBtn].forEach((btn) => {
        if (!btn) return;
        btn.classList.toggle('fpv-is-playing', playing);
        btn.setAttribute('aria-pressed', String(playing));
        btn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      });
    }

    _syncMuteState() {
      const muted = this.video.muted;
      [this.muteBtn, this.lightboxMuteBtn].forEach((btn) => {
        if (!btn) return;
        btn.classList.toggle('fpv-is-muted', muted);
        btn.setAttribute('aria-pressed', String(!muted));
        btn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
      });
    }

    _expand() {
      if (!this.lightbox || !this.lightboxFrame) return;

      this._wasPlayingBeforeExpand = !this.video.paused;
      this._wasMutedBeforeExpand = this.video.muted;
      this._lastFocused = document.activeElement;

      // Move the SAME video element into the lightbox - no duplicate download.
      this.lightboxFrame.appendChild(this.video);

      this.lightbox.hidden = false;
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.addEventListener('keydown', this._onKeydown);
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        if (this.lightboxMinimizeBtn) this.lightboxMinimizeBtn.focus();
      });
    }

    // Returns to the floating mini player. This is intentionally the ONLY
    // way to leave the expanded view (besides Escape/backdrop click) - it
    // is a minimize action, not a close/remove action. Closing the feature
    // entirely is only ever done from the (X) on the mini player.
    _collapse() {
      if (!this.lightbox || !this.miniFrame) return;

      this.miniFrame.appendChild(this.video);

      this.lightbox.hidden = true;
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this._onKeydown);
      document.body.style.overflow = '';

      this.video.muted = this._wasMutedBeforeExpand;
      this._syncMuteState();

      if (this._wasPlayingBeforeExpand) {
        this.video.play().catch(() => {});
      }

      if (this._lastFocused && document.contains(this._lastFocused)) {
        this._lastFocused.focus();
      } else if (this.expandBtn) {
        this.expandBtn.focus();
      }
    }

    _close() {
      // If the customer somehow closes while expanded, make sure the
      // lightbox's side effects (locked body scroll, document keydown
      // listener) are always cleaned up too - never leave the page stuck.
      if (this.lightbox && !this.lightbox.hidden) {
        this._collapse();
      }

      this._closed = true;
      this.video.pause();
      this._stopTracking();
      this.classList.remove('fpv-is-visible');
      this.setAttribute('hidden', '');
    }

    _onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this._collapse();
      }
    }

    // Keyboard alternative to dragging: focus the mini player, use arrow keys.
    _onPlayerKeydown(event) {
      const step = KEYBOARD_NUDGE_STEP;
      let dx = 0;
      let dy = 0;
      if (event.key === 'ArrowLeft') dx = -step;
      else if (event.key === 'ArrowRight') dx = step;
      else if (event.key === 'ArrowUp') dy = -step;
      else if (event.key === 'ArrowDown') dy = step;
      else if (event.key === 'Enter' || event.key === ' ') {
        // Convenient keyboard shortcut to expand, matching a click on the player.
        event.preventDefault();
        this._expand();
        return;
      } else {
        return;
      }

      event.preventDefault();
      this._userPositioned = true;
      const size = this.player.offsetWidth || 250;
      const gap = window.innerWidth < 750 ? 12 : 16;
      this._setPosition(this._x + dx, this._y + dy, size, gap);
      this._applyStickyAvoidance(size, gap);
    }

    // ---------------- Dragging ----------------

    _onPointerDown(event) {
      // Ignore a second pointer while already dragging (e.g. multi-touch).
      if (this._dragging) return;
      // Ignore drags that start on a control button - let those clicks through.
      if (event.target.closest && event.target.closest('button')) return;
      if (event.button !== undefined && event.button !== 0) return;

      this._dragging = true;
      this._dragStartClientX = event.clientX;
      this._dragStartClientY = event.clientY;
      this._dragStartX = this._x;
      this._dragStartY = this._y;

      this.player.classList.add('fpv-is-dragging');
      if (this.player.setPointerCapture) {
        try {
          this.player.setPointerCapture(event.pointerId);
        } catch (e) {
          /* ignore */
        }
      }

      this.player.addEventListener('pointermove', this._onPointerMove);
      this.player.addEventListener('pointerup', this._onPointerUp);
      this.player.addEventListener('pointercancel', this._onPointerUp);
    }

    _onPointerMove(event) {
      if (!this._dragging) return;
      event.preventDefault();

      const size = this.player.offsetWidth || 250;
      const gap = window.innerWidth < 750 ? 12 : 16;
      const dx = event.clientX - this._dragStartClientX;
      const dy = event.clientY - this._dragStartClientY;

      this._setPosition(this._dragStartX + dx, this._dragStartY + dy, size, gap);
    }

    _onPointerUp(event) {
      if (!this._dragging) return;
      this._dragging = false;
      this._userPositioned = true;

      this.player.classList.remove('fpv-is-dragging');
      if (this.player.releasePointerCapture) {
        try {
          this.player.releasePointerCapture(event.pointerId);
        } catch (e) {
          /* ignore */
        }
      }

      this.player.removeEventListener('pointermove', this._onPointerMove);
      this.player.removeEventListener('pointerup', this._onPointerUp);
      this.player.removeEventListener('pointercancel', this._onPointerUp);

      const size = this.player.offsetWidth || 250;
      const gap = window.innerWidth < 750 ? 12 : 16;
      this._applyStickyAvoidance(size, gap);
    }

    // ---------------- Positioning ----------------

    _setPosition(x, y, size, gap) {
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;
      const clampedX = Math.max(gap, Math.min(x, viewportW - size - gap));
      const clampedY = Math.max(gap, Math.min(y, viewportH - size - gap));

      this._x = clampedX;
      this._y = clampedY;
      this.player.style.transform = `translate3d(${Math.round(clampedX)}px, ${Math.round(clampedY)}px, 0)`;
      this.classList.add('fpv-is-visible');
    }

    // Never let the player sit on top of the sticky add-to-cart bar,
    // regardless of whether it got there via auto-tracking or a drag.
    // Some themes keep the sticky bar in the DOM at all times (non-zero
    // size) and animate it off-screen with a transform instead of
    // toggling `hidden`/display, and its `hidden` property/attribute can
    // be unreliable to read - so detect it by whether it is actually
    // docked at the bottom of the viewport right now.
    _applyStickyAvoidance(size, gap) {
      if (!this.stickyForm) return;
      const viewportH = window.innerHeight;
      const stickyRect = this.stickyForm.getBoundingClientRect();
      const stickyDocked =
        stickyRect.height > 0 &&
        stickyRect.top < viewportH &&
        stickyRect.bottom >= viewportH - 20;

      if (stickyDocked && this._y + size > stickyRect.top) {
        const newY = Math.max(gap, stickyRect.top - size - gap);
        this._setPosition(this._x, newY, size, gap);
      }
    }

    _startTracking() {
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      this._updatePosition();
    }

    _stopTracking() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    }

    _onScroll() {
      this._queuePositionUpdate();
    }

    _onResize() {
      this._queuePositionUpdate();
    }

    _queuePositionUpdate() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        try {
          this._updatePosition();
        } catch (error) {
          this._safeFail(error);
        }
      });
    }

    _updatePosition() {
      if (this._closed || !this.gallery || this._dragging) return;

      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;
      const size = this.player.offsetWidth || (viewportW < 750 ? 165 : 250);
      const gap = viewportW < 750 ? 12 : 16;

      if (this._userPositioned) {
        // The customer has taken control of where this lives - respect
        // that and only step in if it would now cover the sticky bar.
        this._applyStickyAvoidance(size, gap);
        this.classList.add('fpv-is-visible');
        return;
      }

      const galleryRect = this.gallery.getBoundingClientRect();

      // Hide while the gallery isn't meaningfully on screen (video is an
      // enhancement to the gallery, it should never wander off on its own).
      const galleryVisible = galleryRect.bottom > 80 && galleryRect.top < viewportH - 80;
      if (!galleryVisible) {
        this.classList.remove('fpv-is-visible');
        return;
      }

      let top = Math.min(galleryRect.bottom, viewportH) - size - gap;
      let left = galleryRect.right - size - gap;

      // Keep inside the viewport horizontally/vertically.
      top = Math.max(gap, Math.min(top, viewportH - size - gap));
      left = Math.max(gap, Math.min(left, viewportW - size - gap));

      // Never sit on top of the sticky add-to-cart bar.
      if (this.stickyForm) {
        const stickyRect = this.stickyForm.getBoundingClientRect();
        const stickyDocked =
          stickyRect.height > 0 &&
          stickyRect.top < viewportH &&
          stickyRect.bottom >= viewportH - 20;
        if (stickyDocked && top + size > stickyRect.top) {
          top = stickyRect.top - size - gap;
        }
      }

      // Never bleed into the next section (e.g. description/reviews).
      if (this.boundaryEl) {
        const boundaryRect = this.boundaryEl.getBoundingClientRect();
        if (boundaryRect.top < viewportH && top + size > boundaryRect.top) {
          top = Math.min(top, boundaryRect.top - size - gap);
        }
      }

      // If there is no safe vertical spot left, hide rather than overlap.
      if (top < gap) {
        this.classList.remove('fpv-is-visible');
        return;
      }

      this._x = left;
      this._y = top;
      this.player.style.transform = `translate3d(${Math.round(left)}px, ${Math.round(top)}px, 0)`;
      this.classList.add('fpv-is-visible');
    }
  }

  try {
    customElements.define('anuka-floating-video', AnukaFloatingVideo);
  } catch (error) {
    // Extremely unlikely (would only happen if something else already
    // defined this exact tag name), but never let registration failure
    // throw an uncaught error into the rest of the theme's scripts.
    if (window.console && typeof console.warn === 'function') {
      console.warn(LOG_PREFIX + ' could not register its custom element:', error);
    }
  }
})();